import AuthLayout from '../components/AuthLayout';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock } from 'lucide-react';
import GoogleSignInButton, { GOOGLE_CLIENT_ID } from '../components/GoogleSignInButton';
import { trackEvent } from '../utils/analytics';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      trackEvent('login', { method: 'email' });
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
          {/* Google sign-in (shown only when configured) */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="mb-4">
                <GoogleSignInButton text="signin_with" onError={setError} />
              </div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-1 h-px bg-green-800/60" />
                <span className="text-xs text-green-400/80">or sign in with email</span>
                <div className="flex-1 h-px bg-green-800/60" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div role="alert" className="bg-red-500/15 text-red-300 border border-red-500/30 p-3 rounded-xl text-sm text-center">
                {error}
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-400/70" />
              <input
                id="email"
                aria-label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full pl-11 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:bg-white/15 focus:ring-2 focus:ring-yellow-500/70 focus:border-transparent outline-none transition"
                placeholder="Email address"
                required
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-400/70" />
              <input
                id="password"
                aria-label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full pl-11 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:bg-white/15 focus:ring-2 focus:ring-yellow-500/70 focus:border-transparent outline-none transition"
                placeholder="Password"
                required
              />
            </div>

            <div className="text-right -mt-1">
              <Link to="/forgot-password" className="text-sm text-green-300/80 hover:text-white">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3.5 rounded-xl font-semibold hover:from-green-500 hover:to-emerald-500 transition active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-green-900/50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center mt-4 text-sm text-green-200/80">
            Don't have an account?{' '}
            <Link to="/register" className="text-yellow-400 font-semibold hover:underline">
              Sign up
            </Link>
          </p>

    </AuthLayout>
  );
};

export default Login;
